import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  GraduationCap, Users, Sparkles, MapPin, Award, Send, Check, X,
  Inbox, SendHorizonal, Target, Clock, Filter, UserCheck
} from "lucide-react";

const ROLE_META = {
  mentor: { label: "Mentor", color: "#10B981", icon: UserCheck },
  mentee: { label: "Mentee", color: "#8DB5A0", icon: GraduationCap },
  both: { label: "Both", color: "#6366F1", icon: Sparkles },
  none: { label: "Not participating", color: "#94A3B8", icon: Users },
};

const ROLE_SEEK = [
  { value: "", label: "All" },
  { value: "mentor", label: "Mentors" },
  { value: "mentee", label: "Mentees" },
];

const MentorshipPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Profile state
  const [myProfile, setMyProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    role: "none", bio: "", years_experience: 0, specialties: [], availability: "", open_to_matches: true
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Meta
  const [specialtiesMeta, setSpecialtiesMeta] = useState([]);
  const [provinces, setProvinces] = useState([]);

  // Directory
  const [directory, setDirectory] = useState([]);
  const [dirLoading, setDirLoading] = useState(false);
  const [filters, setFilters] = useState({ role: "", province: "", specialty: "", min_years: "" });

  // Matches
  const [matches, setMatches] = useState([]);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState("");

  // Requests
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);

  // Connect dialog
  const [connectTarget, setConnectTarget] = useState(null);
  const [connectMsg, setConnectMsg] = useState("");
  const [connectGoals, setConnectGoals] = useState("");
  const [sendingRequest, setSendingRequest] = useState(false);

  // Profile editor dialog
  const [showProfileEdit, setShowProfileEdit] = useState(false);

  // Initial fetches
  useEffect(() => {
    (async () => {
      try {
        const [meta, me, prov] = await Promise.all([
          axios.get(`${API}/mentorship/meta`),
          axios.get(`${API}/mentorship/me`),
          axios.get(`${API}/provinces`).catch(() => ({ data: [] })),
        ]);
        setSpecialtiesMeta(meta.data.specialties || []);
        setMyProfile(me.data);
        setProfileForm({
          role: me.data.role || "none",
          bio: me.data.bio || "",
          years_experience: me.data.years_experience || 0,
          specialties: me.data.specialties || [],
          availability: me.data.availability || "",
          open_to_matches: me.data.open_to_matches !== false,
        });
        setProvinces(prov.data || []);
      } catch {
        toast.error("Failed to load mentorship data");
      }
    })();
    fetchRequests();
  }, []);

  const fetchDirectory = useCallback(async () => {
    setDirLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.role) params.set("role", filters.role);
      if (filters.province) params.set("province", filters.province);
      if (filters.specialty) params.set("specialty", filters.specialty);
      if (filters.min_years) params.set("min_years", filters.min_years);
      const res = await axios.get(`${API}/mentorship/directory?${params.toString()}`);
      setDirectory(res.data);
    } catch {
      toast.error("Failed to load directory");
    } finally {
      setDirLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchDirectory(); }, [fetchDirectory]);

  const fetchRequests = async () => {
    try {
      const [rec, sent] = await Promise.all([
        axios.get(`${API}/mentorship/requests?direction=received`),
        axios.get(`${API}/mentorship/requests?direction=sent`),
      ]);
      setReceivedRequests(rec.data);
      setSentRequests(sent.data);
    } catch (err) {
      console.error("[MentorshipPage] fetchRequests failed", err);
    }
  };

  const runMatching = async () => {
    setMatchLoading(true);
    setMatchError("");
    try {
      const res = await axios.get(`${API}/mentorship/match`);
      setMatches(res.data);
      setMatchesLoaded(true);
      if (res.data.length === 0) {
        toast.info("No strong matches yet — try updating your profile or broadening your specialties.");
      }
    } catch (error) {
      setMatchError(error.response?.data?.detail || "Unable to generate matches right now");
    } finally {
      setMatchLoading(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/mentorship/me`, {
        ...profileForm,
        years_experience: parseInt(profileForm.years_experience) || 0,
      });
      setMyProfile(res.data);
      toast.success("Mentorship profile saved!");
      setShowProfileEdit(false);
      fetchDirectory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const toggleSpecialty = (key) => {
    setProfileForm(f => {
      const has = f.specialties.includes(key);
      if (has) return { ...f, specialties: f.specialties.filter(s => s !== key) };
      if (f.specialties.length >= 6) { toast.info("Max 6 specialties"); return f; }
      return { ...f, specialties: [...f.specialties, key] };
    });
  };

  const openConnect = (member) => {
    setConnectTarget(member);
    setConnectMsg("");
    setConnectGoals("");
  };

  const sendRequest = async () => {
    if (!connectMsg.trim()) { toast.error("Write a short intro message"); return; }
    setSendingRequest(true);
    try {
      await axios.post(`${API}/mentorship/requests`, {
        recipient_id: connectTarget.user_id,
        message: connectMsg,
        goals: connectGoals,
      });
      toast.success(`Request sent to ${connectTarget.name}!`);
      setConnectTarget(null);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setSendingRequest(false);
    }
  };

  const respondRequest = async (reqId, action) => {
    try {
      await axios.post(`${API}/mentorship/requests/${reqId}/respond`, { action });
      toast.success(action === "accept" ? "Request accepted — start the conversation!" : "Request declined");
      fetchRequests();
    } catch {
      toast.error("Failed to update request");
    }
  };

  const specialtyLabel = (key) => (specialtiesMeta.find(s => s.key === key)?.label) || key;
  const hasMentorshipProfile = myProfile && myProfile.role && myProfile.role !== "none";
  const pendingReceived = receivedRequests.filter(r => r.status === "pending").length;

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="mentorship-page">
        {/* Hero */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#6B4E8A] flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-[#8DB5A0]" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <EditableText blockKey="mentorship.title" fallback="Mentorship" as="span" />
              </h1>
            </div>
            <EditableText
              blockKey="mentorship.subtitle"
              fallback="Pair with experienced hygienists, share wisdom with new grads, or explore matches tailored to your goals."
              as="p"
              className="text-[#64748B] max-w-xl"
              multiline
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowProfileEdit(true)} variant="outline" className="rounded-full border-[#E5DFEC]" data-testid="edit-mentorship-profile-btn">
              {hasMentorshipProfile ? "Edit My Profile" : "Set Up My Profile"}
            </Button>
          </div>
        </div>

        {/* My profile banner */}
        {!hasMentorshipProfile && (
          <div className="mb-6 bg-[#FFF7ED] border border-[#FDBA74] rounded-2xl p-4 flex items-start gap-3" data-testid="profile-prompt-banner">
            <Sparkles className="w-5 h-5 text-[#D97706] mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-[#6B4E8A]">Set up your mentorship profile</p>
              <p className="text-sm text-[#64748B]">Tell others whether you're looking to mentor, be mentored, or both. You'll start appearing in the directory and can get personalized matches.</p>
            </div>
            <Button onClick={() => setShowProfileEdit(true)} size="sm" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full shrink-0">Get Started</Button>
          </div>
        )}

        <Tabs defaultValue="directory" className="space-y-6">
          <div
            className="overflow-x-auto -mx-2 px-2 w-full min-w-0 scroll-smooth"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <TabsList className="bg-[#F0ECF7] inline-flex flex-nowrap w-max">
              <TabsTrigger value="directory" className="data-[state=active]:bg-white shrink-0" data-testid="tab-directory"><Users className="w-4 h-4 mr-1.5" />Directory</TabsTrigger>
              <TabsTrigger value="match" className="data-[state=active]:bg-white shrink-0" data-testid="tab-match"><Sparkles className="w-4 h-4 mr-1.5" />Find Match</TabsTrigger>
              <TabsTrigger value="requests" className="data-[state=active]:bg-white relative shrink-0" data-testid="tab-requests">
                <Inbox className="w-4 h-4 mr-1.5" />Requests
                {pendingReceived > 0 && (
                  <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="requests-pending-badge">{pendingReceived}</span>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Directory */}
          <TabsContent value="directory" className="space-y-5">
            {/* Filters */}
            <div className="bg-white border border-[#E5DFEC] rounded-2xl p-4 flex flex-wrap items-center gap-3" data-testid="directory-filters">
              <Filter className="w-4 h-4 text-[#64748B]" />
              <Select value={filters.role || "all"} onValueChange={v => setFilters(f => ({ ...f, role: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[140px] h-9 bg-[#FAF7F2] border-[#E5DFEC] text-sm" data-testid="filter-role"><SelectValue placeholder="Role" /></SelectTrigger>
                <SelectContent>{ROLE_SEEK.map(r => <SelectItem key={r.value || "all"} value={r.value || "all"}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filters.province || "all"} onValueChange={v => setFilters(f => ({ ...f, province: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[160px] h-9 bg-[#FAF7F2] border-[#E5DFEC] text-sm" data-testid="filter-province"><SelectValue placeholder="Province" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Provinces</SelectItem>
                  {provinces.map(p => <SelectItem key={p.code || p} value={p.code || p}>{p.name || p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.specialty || "all"} onValueChange={v => setFilters(f => ({ ...f, specialty: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[190px] h-9 bg-[#FAF7F2] border-[#E5DFEC] text-sm" data-testid="filter-specialty"><SelectValue placeholder="Specialty" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Specialty</SelectItem>
                  {specialtiesMeta.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                type="number" min="0" placeholder="Min years" value={filters.min_years}
                onChange={e => setFilters(f => ({ ...f, min_years: e.target.value }))}
                className="w-[110px] h-9 bg-[#FAF7F2] border-[#E5DFEC] text-sm"
                data-testid="filter-min-years"
              />
              {(filters.role || filters.province || filters.specialty || filters.min_years) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ role: "", province: "", specialty: "", min_years: "" })} className="text-[#8DB5A0] h-9">Clear</Button>
              )}
            </div>

            {/* Cards */}
            {dirLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3,4,5,6].map(i => <div key={i} className="h-56 bg-[#F0ECF7] rounded-2xl animate-pulse" />)}
              </div>
            ) : directory.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {directory.map((m, i) => (
                  <MentorshipCard key={m.user_id} member={m} index={i} onConnect={() => openConnect(m)} onView={() => navigate(`/profile/${m.user_id}`)} specialtyLabel={specialtyLabel} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
                <Users className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No members match your filters</h3>
                <p className="text-[#64748B]">Try clearing filters or encourage more hygienists to join mentorship!</p>
              </div>
            )}
          </TabsContent>

          {/* Find Match */}
          <TabsContent value="match" className="space-y-5">
            <div className="bg-[#6B4E8A] text-white rounded-2xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-[#8DB5A0]" /><span className="text-xs uppercase tracking-wider text-[#8DB5A0] font-medium">Smart Matching</span></div>
                <h2 className="text-2xl font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Find your top 3 mentorship matches</h2>
                <p className="text-white/70 text-sm">We'll look at your province, specialties, experience gap, and engagement to suggest hygienists who'd be a great fit for you.</p>
              </div>
              <Button onClick={runMatching} disabled={matchLoading} className="bg-[#8DB5A0] text-[#6B4E8A] hover:bg-[#C49E2D] rounded-full px-6 h-11 font-medium" data-testid="find-match-btn">
                {matchLoading ? "Finding matches..." : matchesLoaded ? "Refresh matches" : "Find me a match"}
              </Button>
            </div>

            {matchError && (
              <div className="bg-[#FFF7ED] border border-[#FDBA74] rounded-2xl p-4 text-sm text-[#9A3412] flex items-start gap-3" data-testid="match-error">
                <Sparkles className="w-5 h-5 mt-0.5 text-[#D97706] shrink-0" />
                <div>{matchError}</div>
              </div>
            )}

            {matchesLoaded && matches.length === 0 && !matchError && (
              <div className="text-center py-12 bg-white border border-[#E5DFEC] rounded-2xl">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-[#E5DFEC]" />
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>No strong matches yet</h3>
                <p className="text-[#64748B] text-sm">Add specialties or experience years to your profile, or check back soon as more members join.</p>
              </div>
            )}

            {matches.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {matches.map((m, i) => (
                  <MentorshipCard
                    key={m.user_id} member={m} index={i} highlight
                    matchScore={m.match_score}
                    onConnect={() => openConnect(m)}
                    onView={() => navigate(`/profile/${m.user_id}`)}
                    specialtyLabel={specialtyLabel}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Requests */}
          <TabsContent value="requests" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RequestList
                title="Received"
                icon={<Inbox className="w-4 h-4" />}
                requests={receivedRequests}
                emptyLabel="No requests received yet"
                isReceived
                onAccept={(id) => respondRequest(id, "accept")}
                onDecline={(id) => respondRequest(id, "decline")}
                onMessage={(userId) => navigate(`/messages/${userId}`)}
              />
              <RequestList
                title="Sent"
                icon={<SendHorizonal className="w-4 h-4" />}
                requests={sentRequests}
                emptyLabel="You haven't sent any requests yet"
                onMessage={(userId) => navigate(`/messages/${userId}`)}
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Connect Dialog */}
        <Dialog open={!!connectTarget} onOpenChange={(v) => !v && setConnectTarget(null)}>
          <DialogContent className="max-w-md" data-testid="connect-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Send mentorship request</DialogTitle>
              <DialogDescription>
                Introduce yourself to {connectTarget?.name} and share what you're hoping to gain or offer.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Intro message</Label>
                <Textarea value={connectMsg} onChange={e => setConnectMsg(e.target.value)} maxLength={600} placeholder="Hi! I'm a new grad in Calgary focusing on perio..." className="mt-2 min-h-[100px]" data-testid="connect-message" />
                <p className="text-xs text-[#94A3B8] mt-1">{connectMsg.length}/600</p>
              </div>
              <div>
                <Label>Goals (optional)</Label>
                <Textarea value={connectGoals} onChange={e => setConnectGoals(e.target.value)} maxLength={300} placeholder="e.g. Build confidence with anxious patients, or explore clinical educator path" className="mt-2 min-h-[60px]" data-testid="connect-goals" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setConnectTarget(null)} className="rounded-full">Cancel</Button>
                <Button onClick={sendRequest} disabled={!connectMsg.trim() || sendingRequest} className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="send-connect-request">
                  <Send className="w-3.5 h-3.5 mr-1.5" />{sendingRequest ? "Sending..." : "Send request"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Profile Editor Dialog */}
        <Dialog open={showProfileEdit} onOpenChange={setShowProfileEdit}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="mentorship-profile-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>My Mentorship Profile</DialogTitle>
              <DialogDescription>
                Share how you'd like to participate. You can update this anytime.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-sm">I want to participate as</Label>
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {["mentor", "mentee", "both", "none"].map(r => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setProfileForm(f => ({ ...f, role: r }))}
                      className={`text-xs rounded-xl border py-3 px-2 transition-colors ${profileForm.role === r ? "border-transparent text-white" : "border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A] bg-white"}`}
                      style={profileForm.role === r ? { backgroundColor: ROLE_META[r].color } : {}}
                      data-testid={`role-option-${r}`}
                    >
                      {ROLE_META[r].label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Years of experience</Label>
                  <Input type="number" min="0" max="60" value={profileForm.years_experience} onChange={e => setProfileForm(f => ({ ...f, years_experience: e.target.value }))} className="mt-2" data-testid="years-experience-input" />
                </div>
                <div>
                  <Label className="text-sm">Availability</Label>
                  <Input value={profileForm.availability} onChange={e => setProfileForm(f => ({ ...f, availability: e.target.value }))} placeholder="e.g. 1 hr/week, evenings" className="mt-2" data-testid="availability-input" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Bio <span className="text-[#94A3B8] font-normal">(shown to others)</span></Label>
                <Textarea value={profileForm.bio} onChange={e => setProfileForm(f => ({ ...f, bio: e.target.value }))} maxLength={500} placeholder="A couple of sentences about your journey, style, and what you can offer or want to learn." className="mt-2 min-h-[80px]" data-testid="bio-input" />
                <p className="text-xs text-[#94A3B8] mt-1">{profileForm.bio.length}/500</p>
              </div>
              <div>
                <Label className="text-sm">Specialties <span className="text-[#94A3B8] font-normal">(pick up to 6)</span></Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {specialtiesMeta.map(s => {
                    const on = profileForm.specialties.includes(s.key);
                    return (
                      <button
                        key={s.key} type="button" onClick={() => toggleSpecialty(s.key)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "bg-[#6B4E8A] text-white border-[#6B4E8A]" : "border-[#E5DFEC] text-[#64748B] hover:border-[#6B4E8A]"}`}
                        data-testid={`specialty-${s.key}`}
                      >{s.label}</button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Checkbox id="open-matches" checked={profileForm.open_to_matches} onCheckedChange={c => setProfileForm(f => ({ ...f, open_to_matches: !!c }))} />
                <Label htmlFor="open-matches" className="cursor-pointer text-sm">Show me in the public directory & allow match suggestions</Label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowProfileEdit(false)} className="rounded-full">Cancel</Button>
                <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="save-mentorship-profile">
                  {savingProfile ? "Saving..." : "Save profile"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// --- Subcomponents ---

const MentorshipCard = ({ member, index, onConnect, onView, specialtyLabel, highlight, matchScore }) => {
  const meta = ROLE_META[member.mentorship_role] || ROLE_META.none;
  const Icon = meta.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}
      className={`bg-white border rounded-2xl p-5 flex flex-col relative overflow-hidden ${highlight ? "border-[#8DB5A0] shadow-[0_4px_16px_rgba(212,175,55,0.15)]" : "border-[#E5DFEC]"}`}
      data-testid={`mentorship-card-${member.user_id}`}
    >
      {highlight && matchScore !== undefined && (
        <div className="absolute top-0 right-0 bg-[#8DB5A0] text-[#6B4E8A] text-[10px] font-bold px-2.5 py-1 rounded-bl-xl flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> {matchScore} match
        </div>
      )}
      <div className="flex items-start gap-3 mb-3">
        {member.picture ? (
          <img src={member.picture} alt={member.name} className="w-12 h-12 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center font-medium shrink-0">{member.name?.[0] || "M"}</div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[#6B4E8A] truncate">{member.name}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <span className="text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
              <Icon className="w-3 h-3" />{meta.label}
            </span>
            {member.province && <span className="text-[11px] text-[#64748B] inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{member.province}</span>}
            {member.years_experience > 0 && <span className="text-[11px] text-[#64748B] inline-flex items-center gap-0.5"><Award className="w-3 h-3" />{member.years_experience}y</span>}
          </div>
        </div>
      </div>
      {member.bio && <p className="text-sm text-[#475569] line-clamp-3 mb-3">{member.bio}</p>}
      {member.specialties?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {member.specialties.slice(0, 4).map(s => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#64748B]">{specialtyLabel(s)}</span>
          ))}
          {member.specialties.length > 4 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0ECF7] text-[#64748B]">+{member.specialties.length - 4}</span>}
        </div>
      )}
      {member.availability && (
        <p className="text-xs text-[#64748B] flex items-center gap-1 mb-3"><Clock className="w-3 h-3" />{member.availability}</p>
      )}
      <div className="flex gap-2 mt-auto">
        <Button onClick={onView} variant="outline" size="sm" className="rounded-full flex-1 text-xs h-8 border-[#E5DFEC]" data-testid={`view-profile-${member.user_id}`}>View profile</Button>
        <Button onClick={onConnect} size="sm" className="rounded-full flex-1 text-xs h-8 bg-[#6B4E8A] text-white hover:bg-[#553D6F]" data-testid={`connect-${member.user_id}`}>
          <Send className="w-3 h-3 mr-1" />Connect
        </Button>
      </div>
    </motion.div>
  );
};

const RequestList = ({ title, icon, requests, emptyLabel, isReceived, onAccept, onDecline, onMessage }) => {
  const statusMeta = { pending: { color: "#D97706", label: "Pending" }, accepted: { color: "#10B981", label: "Accepted" }, declined: { color: "#94A3B8", label: "Declined" } };
  return (
    <div className="bg-white border border-[#E5DFEC] rounded-2xl p-5">
      <h3 className="font-medium text-[#6B4E8A] flex items-center gap-2 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{icon}{title} <span className="text-xs text-[#94A3B8] font-normal">({requests.length})</span></h3>
      {requests.length === 0 ? (
        <p className="text-sm text-[#94A3B8] text-center py-8">{emptyLabel}</p>
      ) : (
        <div className="space-y-3">
          {requests.map(r => {
            const sMeta = statusMeta[r.status] || statusMeta.pending;
            const otherName = isReceived ? r.sender_name : r.recipient_name;
            const otherId = isReceived ? r.sender_id : r.recipient_id;
            return (
              <div key={r.request_id} className="border border-[#E5DFEC] rounded-xl p-3" data-testid={`request-${r.request_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-[#8DB5A0] text-white flex items-center justify-center text-xs font-medium shrink-0">{otherName?.[0] || "M"}</div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#6B4E8A] truncate">{otherName}</p>
                      <p className="text-[10px] text-[#94A3B8]">{new Date(r.created_at).toLocaleDateString("en-CA")}</p>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ backgroundColor: `${sMeta.color}18`, color: sMeta.color }}>{sMeta.label}</span>
                </div>
                <p className="text-sm text-[#475569] mb-2 whitespace-pre-wrap">{r.message}</p>
                {r.goals && <p className="text-xs text-[#64748B] bg-[#FAF7F2] rounded-lg p-2 mb-2 flex items-start gap-1.5"><Target className="w-3 h-3 mt-0.5 shrink-0 text-[#8DB5A0]" /><span>{r.goals}</span></p>}
                <div className="flex gap-2">
                  {isReceived && r.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => onAccept(r.request_id)} className="rounded-full bg-[#10B981] text-white hover:bg-[#059669] h-8 text-xs" data-testid={`accept-${r.request_id}`}>
                        <Check className="w-3.5 h-3.5 mr-1" />Accept
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => onDecline(r.request_id)} className="rounded-full h-8 text-xs" data-testid={`decline-${r.request_id}`}>
                        <X className="w-3.5 h-3.5 mr-1" />Decline
                      </Button>
                    </>
                  )}
                  {r.status === "accepted" && (
                    <Button size="sm" variant="outline" onClick={() => onMessage(otherId)} className="rounded-full h-8 text-xs border-[#6B4E8A] text-[#6B4E8A]">
                      <Send className="w-3 h-3 mr-1" />Message
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MentorshipPage;
